import { useEffect, useState, useCallback } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import PageMeta from '../../components/common/PageMeta';
import {
  getDashboard,
  getTrends,
  getTrendsHeatmap,
  getRecommendations,
  DashboardData,
  TrendPattern,
  HeatmapEntry,
  Recommendation,
  RejectionRecord,
} from '../../lib/api/intelligence';
import RejectionTrendsTile from '../../components/intelligence/RejectionTrendsTile';
import RejectionHeatmap from '../../components/intelligence/RejectionHeatmap';
import TrendingPatternsPanel from '../../components/intelligence/TrendingPatternsPanel';
import RecommendationEffectiveness from '../../components/intelligence/RecommendationEffectiveness';
import RejectionDetailPanel from '../../components/intelligence/RejectionDetailPanel';
import RejectionAdvisoryPanel from '../../components/intelligence/RejectionAdvisoryPanel';

interface Filters {
  form_type?: string;
  state?: string;
  district?: string;
}

export default function Intelligence() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [trends, setTrends] = useState<TrendPattern[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapEntry[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [rejections, setRejections] = useState<RejectionRecord[]>([]);
  const [selectedRejectionId, setSelectedRejectionId] = useState<string | null>(null);

  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [loadingHeatmap, setLoadingHeatmap] = useState(true);
  const [loadingRecs, setLoadingRecs] = useState(true);

  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>({});

  const loadData = useCallback(async (f: Filters) => {
    const params: Record<string, string> = {};
    if (f.form_type) params.form_type = f.form_type;
    if (f.state) params.state = f.state;
    if (f.district) params.district = f.district;

    // Dashboard
    setLoadingDashboard(true);
    getDashboard()
      .then((data) => {
        setDashboard(data);
        setRejections(data.recent_rejections || []);
      })
      .catch((err: Error) => setDashboardError(err.message))
      .finally(() => setLoadingDashboard(false));

    // Trends
    setLoadingTrends(true);
    getTrends(params)
      .then(setTrends)
      .catch(() => setTrends([]))
      .finally(() => setLoadingTrends(false));

    // Heatmap
    setLoadingHeatmap(true);
    getTrendsHeatmap(params)
      .then(setHeatmap)
      .catch(() => setHeatmap([]))
      .finally(() => setLoadingHeatmap(false));

    // Recommendations
    setLoadingRecs(true);
    getRecommendations(params)
      .then(setRecommendations)
      .catch(() => setRecommendations([]))
      .finally(() => setLoadingRecs(false));
  }, []);

  useEffect(() => {
    loadData(filters);
  }, []);

  const handleGlobalFilterApply = () => {
    loadData(filters);
  };

  const handleHeatmapFilter = (f: { form_type?: string; state?: string }) => {
    const next = { ...filters, ...f };
    setFilters(next);
    setLoadingHeatmap(true);
    const params: Record<string, string> = {};
    if (next.form_type) params.form_type = next.form_type;
    if (next.state) params.state = next.state;
    getTrendsHeatmap(params)
      .then(setHeatmap)
      .catch(() => setHeatmap([]))
      .finally(() => setLoadingHeatmap(false));
  };

  const selectedRejection = rejections.find((r) => r.id === selectedRejectionId) ?? null;

  return (
    <>
      <PageMeta
        title="Regulatory Intelligence | RegulAgent"
        description="Analytics and insights for regulatory filings"
      />
      <PageBreadcrumb pageTitle="Regulatory Intelligence" />

      {/* Global Filter Bar */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Form Type
            </label>
            <input
              type="text"
              placeholder="e.g. W-3A"
              value={filters.form_type ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, form_type: e.target.value || undefined }))}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 w-32"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              State
            </label>
            <input
              type="text"
              placeholder="TX / NM"
              value={filters.state ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value || undefined }))}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 w-24"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              District
            </label>
            <input
              type="text"
              placeholder="e.g. 7C"
              value={filters.district ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, district: e.target.value || undefined }))}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white placeholder-gray-400 w-24"
            />
          </div>
          <button
            onClick={handleGlobalFilterApply}
            className="px-4 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Dashboard Summary KPIs */}
      {loadingDashboard && (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
        </div>
      )}

      {!loadingDashboard && dashboardError && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
          Failed to load dashboard: {dashboardError}
        </div>
      )}

      {!loadingDashboard && dashboard && (
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-6">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
              Total Filings
            </p>
            <p className="text-3xl font-bold text-gray-800 dark:text-white/90">
              {dashboard.total_filings}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-6">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
              Total Rejections
            </p>
            <p className="text-3xl font-bold text-red-600 dark:text-red-400">
              {dashboard.total_rejections}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-6">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
              Approval Rate
            </p>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {Math.round(dashboard.approval_rate * 100)}%
            </p>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {dashboard && (
          <RejectionTrendsTile
            reasons={dashboard.top_rejection_reasons}
            loading={loadingDashboard}
          />
        )}
        <TrendingPatternsPanel patterns={trends} loading={loadingTrends} />
      </div>

      <div className="mb-6">
        <RejectionHeatmap
          entries={heatmap}
          loading={loadingHeatmap}
          onFilterChange={handleHeatmapFilter}
        />
      </div>

      <div className="mb-6">
        <RecommendationEffectiveness recommendations={recommendations} loading={loadingRecs} />
      </div>

      {/* Recent Rejections Table */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="border-b border-gray-200 dark:border-white/[0.05] px-6 py-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            Recent Rejections
          </h3>
        </div>
        <div className="p-6">
          {rejections.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No recent rejections.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Agency
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Form
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      State
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Issues
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Date
                    </th>
                    <th className="py-2 px-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {rejections.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                      <td className="py-2 px-3 font-medium text-gray-800 dark:text-white/90">
                        {r.agency}
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{r.form_type}</td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{r.state}</td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                        {r.parsed_issues.length}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${
                            r.parse_status === 'verified'
                              ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                              : r.parse_status === 'parsed'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {r.parse_status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">
                        {r.rejection_date
                          ? new Date(r.rejection_date).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() =>
                            setSelectedRejectionId((id) => (id === r.id ? null : r.id))
                          }
                          className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                        >
                          {selectedRejectionId === r.id ? 'Close' : 'Review & Fix'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Detail Drawer */}
      {selectedRejection && (
        <div className="mb-6">
          {selectedRejection.parse_status !== 'pending' ? (
            <RejectionAdvisoryPanel
              rejection={selectedRejection}
              onCorrectionsApplied={(updated) => {
                setRejections((prev) =>
                  prev.map((r) => (r.id === updated.id ? updated : r))
                );
                setSelectedRejectionId(updated.id);
              }}
            />
          ) : (
            <RejectionDetailPanel
              rejection={selectedRejection}
              onVerified={(updated) => {
                setRejections((prev) =>
                  prev.map((r) => (r.id === updated.id ? updated : r))
                );
              }}
            />
          )}
        </div>
      )}
    </>
  );
}
